import { jsx, jsxs } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Box, Stack, FieldLabel, HiddenVisually, IconTick, FieldMessage, IconChevron } from "braid-design-system";
import { useRef, useState, useCallback, useEffect } from "react";
import { translations } from "../../../../components/PrivacyDropdown/FancyDropdown/.vocab/index.mjs";
import { dropdownAsButton, dropdown, dropdownBorder, invalidDropdownBox, dropdownSelectedText, dropdownBootstrap, buttonDropdownBootstrap, hidden, dropdownBootstrapButton, tickHidden, tick, buttonChevron, chevron } from "./FancyDropdown.css.mjs";
const isElementInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth);
};
const FancyDropdown = ({
  id,
  valid,
  value,
  selectionTypeString,
  placeholder,
  options,
  onToggle,
  dropdownToggleComponent,
  description,
  label,
  secondaryLabel,
  tertiaryLabel,
  message,
  messageProps,
  onChange,
  dropdownListRef: inputDropdownListRef,
  dropdownButtonRef: inputDropdownButtonRef
}) => {
  var _a, _b;
  const { t } = useTranslations(translations);
  const initialRender = useRef(true);
  const defaultListRef = useRef(null);
  const defaultButtonRef = useRef(null);
  const dropdownListRef = inputDropdownListRef || defaultListRef;
  const dropdownButtonRef = inputDropdownButtonRef || defaultButtonRef;
  const dropdownListOptionZeroRef = useRef(null);
  const dropdownListOptionOneRef = useRef(null);
  const dropdownListOptionTwoRef = useRef(null);
  const dropdownListOptionsRefs = [
    dropdownListOptionZeroRef,
    dropdownListOptionOneRef,
    dropdownListOptionTwoRef
  ];
  const isButtonDropDown = Boolean(dropdownToggleComponent === "button");
  const buttonDropDownLabel = label || t("Select");
  const translatedValue = value ? t(value) : void 0;
  const selectDropDownLabel = translatedValue || placeholder;
  const [dropdownListVisible, setDropdownListVisible] = useState(false);
  const [focusedElement, setFocusedElement] = useState(-1);
  const handleClickOutside = useCallback(
    (event) => {
      var _a2, _b2;
      if (!((_a2 = dropdownListRef == null ? void 0 : dropdownListRef.current) == null ? void 0 : _a2.contains(
        // @ts-expect-error - `EventTarget` is not assignable to `Node`
        event.target
      )) && !((_b2 = dropdownButtonRef == null ? void 0 : dropdownButtonRef.current) == null ? void 0 : _b2.contains(
        // @ts-expect-error - `EventTarget` is not assignable to `Node`
        event.target
      )) && dropdownListVisible) {
        setDropdownListVisible(false);
      }
    },
    [dropdownButtonRef, dropdownListRef, dropdownListVisible]
  );
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);
  const dropdownButtonFocus = useCallback(
    (element) => setFocusedElement(element),
    []
  );
  useEffect(() => {
    var _a2, _b2;
    if (!initialRender.current) {
      if (dropdownListVisible) {
        (_a2 = dropdownListRef == null ? void 0 : dropdownListRef.current) == null ? void 0 : _a2.focus();
      } else if (dropdownButtonRef.current && isElementInViewport(dropdownButtonRef.current)) {
        (_b2 = dropdownButtonRef.current) == null ? void 0 : _b2.focus();
      }
    }
  }, [dropdownButtonRef, dropdownListRef, dropdownListVisible]);
  useEffect(() => {
    if (initialRender.current === true) {
      initialRender.current = false;
    }
  }, []);
  const dropdownButtonClick = () => {
    const newDropdownListVisible = !dropdownListVisible;
    setDropdownListVisible(newDropdownListVisible);
    onToggle == null ? void 0 : onToggle(newDropdownListVisible);
  };
  const dropdownListClick = useCallback(
    (newValue) => {
      if (onChange) {
        onChange(newValue);
      }
      setDropdownListVisible(false);
    },
    [onChange]
  );
  const dropdownListKeyDown = (e) => {
    var _a2, _b2;
    const keys = {
      ESC: 27,
      UP: 38,
      DOWN: 40
    };
    switch (e.keyCode) {
      case keys.ESC:
        setDropdownListVisible(false);
        break;
      case keys.UP:
        e.preventDefault();
        e.stopPropagation();
        if (focusedElement > 0) {
          (_a2 = dropdownListOptionsRefs[focusedElement - 1].current) == null ? void 0 : _a2.focus();
        }
        break;
      case keys.DOWN:
        e.preventDefault();
        e.stopPropagation();
        if (focusedElement < options.length - 1) {
          (_b2 = dropdownListOptionsRefs[focusedElement + 1].current) == null ? void 0 : _b2.focus();
        }
        break;
    }
  };
  const renderChevron = () => {
    if (dropdownToggleComponent === "button") {
      return /* @__PURE__ */ jsx(Box, { className: buttonChevron, children: /* @__PURE__ */ jsx(
        IconChevron,
        {
          tone: "link",
          size: "small",
          direction: dropdownListVisible ? "up" : "down"
        }
      ) });
    }
    return /* @__PURE__ */ jsx(Box, { className: chevron, children: /* @__PURE__ */ jsx(
      IconChevron,
      {
        size: "small",
        direction: dropdownListVisible ? "up" : "down"
      }
    ) });
  };
  return /* @__PURE__ */ jsx(Box, { position: "relative", children: /* @__PURE__ */ jsxs(Stack, { space: isButtonDropDown ? "none" : "xsmall", children: [
    /* @__PURE__ */ jsx(
      FieldLabel,
      {
        ...{
          id,
          label: /* @__PURE__ */ jsxs("span", { children: [
            isButtonDropDown ? null : label,
            value ? /* @__PURE__ */ jsxs(HiddenVisually, { children: [
              " ",
              ", currently selected: ",
              value
            ] }) : null
          ] }),
          htmlFor: id,
          secondaryLabel,
          tertiaryLabel,
          description
        }
      }
    ),
    /* @__PURE__ */ jsxs(Box, { position: "relative", children: [
      !isButtonDropDown ? renderChevron() : null,
      /* @__PURE__ */ jsxs(
        Box,
        {
          component: "button",
          className: [
            isButtonDropDown ? dropdownAsButton : dropdown,
            !isButtonDropDown ? dropdownBorder : "",
            !valid ? invalidDropdownBox : "",
            dropdownSelectedText
          ],
          "data-automation": "privacy-change",
          ref: dropdownButtonRef,
          onClick: dropdownButtonClick,
          type: "button",
          id,
          "aria-expanded": dropdownListVisible,
          "aria-label": value ? (_a = t("Current selection: value", {
            selectionTypeString,
            value
          })) == null ? void 0 : _a.toString() : placeholder,
          onKeyDown: dropdownListKeyDown,
          children: [
            isButtonDropDown ? buttonDropDownLabel : selectDropDownLabel,
            isButtonDropDown ? renderChevron() : null
          ]
        }
      ),
      /* @__PURE__ */ jsx(
        Box,
        {
          component: "ul",
          ref: dropdownListRef,
          className: [
            dropdownBootstrap,
            isButtonDropDown ? buttonDropdownBootstrap : "",
            !dropdownListVisible ? hidden : ""
          ],
          "aria-label": (_b = t("Select selection type", {
            selectionTypeString
          })) == null ? void 0 : _b.toString(),
          tabIndex: -1,
          onKeyDown: dropdownListKeyDown,
          children: options.map((option, index) => /* @__PURE__ */ jsx(Box, { component: "li", "data-automation": "privacy-option", children: /* @__PURE__ */ jsxs(
            Box,
            {
              type: "button",
              component: "button",
              ref: dropdownListOptionsRefs[index],
              onClick: () => dropdownListClick(option.value),
              "aria-selected": value === option.value,
              id: `${id}-${option.value}`,
              className: dropdownBootstrapButton,
              onFocus: () => dropdownButtonFocus(index),
              children: [
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    className: {
                      [tick]: true,
                      [tickHidden]: value !== option.value
                    },
                    children: /* @__PURE__ */ jsx(IconTick, { tone: "formAccent" })
                  }
                ),
                option.label
              ]
            }
          ) }, index))
        }
      )
    ] }),
    message ? /* @__PURE__ */ jsx(
      FieldMessage,
      {
        ...{
          id: `${id}-message`,
          tone: "critical",
          message,
          messageProps
        }
      }
    ) : null
  ] }) });
};
export {
  FancyDropdown
};
